// ════════════════════════════════════════════════════════════════════════════
// Beskar Jobs — Web Component (Accordion Model)
// White-label embeddable job board with integrated verification
// Usage: <script src="https://beskar.io/embed.js" data-org="kessel-run"></script>
// ════════════════════════════════════════════════════════════════════════════

import type { EmbedConfig, Job, ApplicationData, ApplyResponse } from './types';
import { fetchConfig, submitApplication } from './api';
import { buildStyles } from './styles';
import { esc, formatSalary, isValidEmail, debounce, slugFromLocation } from './utils';

class BeskarJobs extends HTMLElement {
  private _config: EmbedConfig | null = null;
  private _filteredJobs: Job[] = [];
  private _expandedSlug: string | null = null; // which job accordion is open
  private _applyingSlug: string | null = null; // which job has apply form open
  // _submittedSlug tracked via _showSubmitted
  private _searchQuery = '';
  private _filterClearance = '';
  // candidateId/token stored for potential future use (gap questions, etc.)

  private _onPopState = (): void => this._syncFromLocation();

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    window.addEventListener('popstate', this._onPopState);
    this._init();
  }

  disconnectedCallback(): void {
    window.removeEventListener('popstate', this._onPopState);
  }

  // ── Deep-link sync ──

  private _syncFromLocation(): void {
    if (!this._config) return;
    const slug = slugFromLocation(window.location);
    const match = slug && this._config.jobs.find(j => j.slug === slug);
    const next = match ? match.slug : null;
    if (next !== this._expandedSlug) {
      this._expandedSlug = next;
      this._applyingSlug = null;
      this._renderAll();
      if (next) {
        const card = this.shadowRoot!.querySelector(`.job-card[data-slug="${next}"]`);
        if (card) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  private _updateUrlForSlug(slug: string | null): void {
    try {
      const url = new URL(window.location.href);
      if (slug) {
        url.hash = '#' + slug;
      } else if (url.hash) {
        url.hash = '';
      } else {
        return;
      }
      // Use pushState so back-button returns to the previous view.
      history.pushState(null, '', url.toString());
    } catch {
      // History API failure (e.g., file://) — silent fallback, no URL change.
    }
  }

  static get observedAttributes(): string[] { return ['org', 'theme']; }

  attributeChangedCallback(name: string, _old: string | null, val: string | null): void {
    if (name === 'org' && val && val !== _old) this._init();
  }

  // ── Init ──

  private async _init(): Promise<void> {
    const org = this.getAttribute('org') || this._getOrgFromScript();
    if (!org) return this._showError('Missing org. Add data-org to the script tag.');

    this._renderLoading();
    try {
      this._config = await fetchConfig(org);
      this._filteredJobs = this._config.jobs;
      this._applyTheme();
      const slug = slugFromLocation(window.location);
      if (slug && this._config.jobs.some(j => j.slug === slug)) {
        this._expandedSlug = slug;
      }
      this._renderAll();
      if (this._expandedSlug) {
        const card = this.shadowRoot!.querySelector(`.job-card[data-slug="${this._expandedSlug}"]`);
        if (card) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch {
      this._showError('Could not load jobs.');
    }
  }

  private _getOrgFromScript(): string {
    const s = document.querySelector('script[data-org]');
    return s?.getAttribute('data-org') || '';
  }

  // ── Theme ──

  private _applyTheme(): void {
    if (!this._config) return;
    const { brandColor } = this._config.org;
    let { mode } = this._config.theme;

    if (brandColor) this.style.setProperty('--beskar-brand', brandColor);

    if (mode === 'auto') {
      const bg = getComputedStyle(document.body).backgroundColor;
      const match = bg.match(/\d+/g);
      if (match) {
        const [r, g, b] = match.map(Number);
        mode = (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? 'light' : 'dark';
      } else { mode = 'light'; }
    }

    if (mode === 'light') {
      this.style.setProperty('--beskar-bg', '#ffffff');
      this.style.setProperty('--beskar-surface', '#f8f9fb');
      this.style.setProperty('--beskar-surface2', '#f0f1f5');
      this.style.setProperty('--beskar-border', 'rgba(0,0,0,0.1)');
      this.style.setProperty('--beskar-text', '#1a1a2e');
      this.style.setProperty('--beskar-dim', '#4a4a5a');
      this.style.setProperty('--beskar-muted', '#7a7a8a');
      this.style.setProperty('--beskar-pink', '#a855f7');
      this.style.setProperty('--beskar-teal', '#0d9488');
      this.style.setProperty('--beskar-green', '#16a34a');
    }
  }

  // ── Filtering ──

  private _applyFilters(): void {
    if (!this._config) return;
    const q = this._searchQuery.toLowerCase();
    const cl = this._filterClearance;
    this._filteredJobs = this._config.jobs.filter(j => {
      if (q && !j.title.toLowerCase().includes(q) && !j.skills.some(s => s.toLowerCase().includes(q)) && !j.location.toLowerCase().includes(q)) return false;
      if (cl && j.clearance !== cl) return false;
      return true;
    });
    // Only update job list + count — don't rebuild header/filters (preserves input focus)
    const root = this.shadowRoot!;
    const listEl = root.querySelector('.job-list');
    const countEl = root.querySelector('.job-count');
    if (listEl && countEl) {
      countEl.textContent = `${this._filteredJobs.length} open position${this._filteredJobs.length !== 1 ? 's' : ''}`;
      this._renderJobListOnly(listEl as HTMLElement);
    } else {
      this._renderAll();
    }
  }

  private _renderJobListOnly(container: HTMLElement): void {
    const sortedJobs = [...this._filteredJobs].sort((a, b) => {
      const aBeta = a.slug === 'cloud-security-engineer' ? 1 : 0;
      const bBeta = b.slug === 'cloud-security-engineer' ? 1 : 0;
      return aBeta - bBeta;
    });

    let html = '';
    for (const j of sortedJobs) {
      const isBeta = j.slug === 'cloud-security-engineer';
      html += `<div class="job-card ${isBeta ? 'job-beta' : ''}" data-slug="${esc(j.slug)}">`;
      if (isBeta) html += `<div class="beta-badge">BETA</div>`;
      html += `<div class="job-card-header" data-toggle="${esc(j.slug)}">
        <div class="job-card-left">
          <div class="job-title">${esc(j.title)}</div>
          <div class="job-meta">
            <span class="job-meta-item">${esc(j.location || 'Location TBD')}</span>
            ${j.workType ? `<span class="job-meta-sep">·</span><span class="job-meta-item">${esc(j.workType)}</span>` : ''}
            ${j.clearance && j.clearance !== 'None' ? `<span class="clearance-badge">${esc(j.clearance)}</span>` : ''}
            ${j.salaryMin || j.salaryMax ? `<span class="salary">${formatSalary(j.salaryMin, j.salaryMax, j.salaryType)}</span>` : ''}
          </div>
          ${j.skills.length > 0 ? `<div class="job-skills">${j.skills.slice(0, 6).map(s => `<span class="skill-tag">${esc(s)}</span>`).join('')}</div>` : ''}
        </div>
        <span class="job-chevron">▸</span>
      </div>`;
      html += `<div class="job-powered">⬡ Powered by <a href="https://beskar.io" target="_blank" rel="noopener">Beskar</a></div>`;
      html += `</div>`;
    }

    if (sortedJobs.length === 0) {
      html = `<div class="empty"><div class="empty-icon">⬡</div>No jobs match your search.</div>`;
    }

    container.innerHTML = html;
    // Rebind click events on new cards
    container.querySelectorAll('[data-toggle]').forEach(el => {
      el.addEventListener('click', () => {
        this._toggleJob((el as HTMLElement).dataset.toggle!);
      });
    });
  }

  // ── Toggle Accordion ──

  private _toggleJob(slug: string): void {
    if (this._expandedSlug === slug) {
      this._expandedSlug = null;
      this._applyingSlug = null;
      this._updateUrlForSlug(null);
    } else {
      this._expandedSlug = slug;
      this._applyingSlug = null;
      this._updateUrlForSlug(slug);
    }
    this._renderAll();
  }

  private _showApplyForm(slug: string): void {
    this._applyingSlug = slug;
    this._renderAll();
    // Scroll the job card into view
    const card = this.shadowRoot!.querySelector(`.job-card[data-slug="${slug}"]`);
    if (card) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ── Apply ──

  private async _handleApply(form: HTMLFormElement, job: Job): Promise<void> {
    const get = (name: string) => (form.querySelector(`[name="${name}"]`) as HTMLInputElement)?.value?.trim() || '';
    const data: ApplicationData = {
      name: get('name'), email: get('email'), employer: get('employer'),
      city: get('city'), clearance: get('clearance'), linkedin: get('linkedin'),
      consent: (form.querySelector('[name="consent"]') as HTMLInputElement)?.checked || false,
      answers: {},
    };
    form.querySelectorAll('[data-question-id]').forEach(el => {
      data.answers[(el as HTMLElement).dataset.questionId!] = (el as HTMLInputElement).value;
    });

    if (!data.name || data.name.length < 2) return this._formError(form, 'Name is required');
    if (!isValidEmail(data.email)) return this._formError(form, 'Valid email is required');
    if (!data.consent) return this._formError(form, 'Please accept the consent');

    // Check honeypot
    if (get('website')) return;

    // Resume is required — surface a clear error if no file was attached.
    // Previously the form would silently submit with no resume, the backend
    // would store an empty row, and the candidate would see 'verified' with
    // alloy_score 0 and no idea why.
    const fileInput = form.querySelector('input[name="resume"]') as HTMLInputElement;
    if (!fileInput?.files?.[0]) {
      return this._formError(form, 'Resume is required — please attach a PDF or DOCX before submitting.');
    }
    const file = fileInput.files[0];
    if (file.size > 8 * 1024 * 1024) {
      return this._formError(form, 'Resume file is too large (max 8MB). Please save it as a smaller PDF and try again.');
    }
    if (file.size === 0) {
      return this._formError(form, 'Resume file appears to be empty. Please re-attach it.');
    }

    const btn = form.querySelector('.form-submit') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'Processing resume...';

    // Read the file → base64 for the backend. Failures here block submission
    // and surface to the user instead of being swallowed silently.
    try {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let binary = '';
      const CHUNK = 0x8000;
      for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
      }
      const encoded = btoa(binary);
      if (!encoded || encoded.length < 100) {
        btn.disabled = false;
        btn.textContent = 'Submit Application';
        return this._formError(form, "Couldn't read the resume contents. Please try a different file.");
      }
      (data as Record<string, unknown>).resumeFileBase64 = encoded;
      (data as Record<string, unknown>).resumeFileType = file.type || 'application/pdf';
      (data as Record<string, unknown>).resumeFileName = file.name;
      if (file.name.endsWith('.txt')) {
        data.resumeText = (await file.text()).slice(0, 20000);
      }
    } catch (e) {
      btn.disabled = false;
      btn.textContent = 'Submit Application';
      console.warn('[BeskarJobs] Resume read error:', (e as Error).message);
      return this._formError(form, "Couldn't read your resume — " + (e as Error).message);
    }

    btn.textContent = 'Submitting...';

    try {
      const result: ApplyResponse = await submitApplication(this._config!.org.slug, job.slug, data);
      // Result stored for potential future use
      this._applyingSlug = null;

      this.dispatchEvent(new CustomEvent('beskar-apply', {
        detail: { candidateId: result.candidateId, job: job.slug },
        bubbles: true, composed: true,
      }));

      // Show simple confirmation — no verification status shown to candidate.
      // Also surface the candidate's Alloy ID share-code so they can build out
      // their profile post-application (per the always-route-to-improve rule).
      this._showSubmitted(job, result.shareCode);
    } catch (e) {
      btn.disabled = false;
      btn.textContent = 'Apply Now';
      this._formError(form, (e as Error).message);
    }
  }

  private _formError(form: HTMLFormElement, msg: string): void {
    let el = form.querySelector('.form-error') as HTMLElement;
    if (!el) { el = document.createElement('div'); el.className = 'form-error'; form.querySelector('.form-submit')?.before(el); }
    el.textContent = msg;
  }

  // Polling/verification feed removed — candidates don't see verification status

  // ── Render ──

  private _renderLoading(): void {
    this.shadowRoot!.innerHTML = `<style>${buildStyles()}</style><div class="wrapper"><div class="loading"><div class="spin"></div></div></div>`;
  }

  private _showError(msg: string): void {
    this.shadowRoot!.innerHTML = `<style>${buildStyles()}</style><div class="wrapper"><div class="error"><div class="error-title">Something went wrong</div><p>${esc(msg)}</p></div></div>`;
  }

  private _renderAll(): void {
    const root = this.shadowRoot!;
    const cfg = this._config!;
    const { name, logo, tagline } = cfg.org;
    const clearances = [...new Set(cfg.jobs.map(j => j.clearance).filter(Boolean))];

    let html = `<style>${buildStyles()}</style><div class="wrapper">`;

    // Header
    html += `<div class="header">
      ${logo ? `<img class="header-logo" src="${esc(logo)}" alt="${esc(name)}">` : ''}
      <div class="header-text"><div class="header-name" ${!logo ? 'style="font-family:var(--mono);font-size:14px;letter-spacing:0.12em;text-transform:uppercase"' : ''}>${esc(name)}</div>${tagline ? `<div class="header-tagline">${esc(tagline)}</div>` : ''}</div>
      <span class="header-badge">Hiring</span>
    </div>`;

    // Filters
    html += `<div class="filters">
      <input class="filter-input" id="search" type="text" placeholder="Search jobs, skills, locations..." value="${esc(this._searchQuery)}">
      ${clearances.length > 0 ? `<select class="filter-select" id="filter-clearance">
        <option value="">All clearances</option>
        ${clearances.map(c => `<option value="${esc(c)}" ${this._filterClearance === c ? 'selected' : ''}>${esc(c)}</option>`).join('')}
      </select>` : ''}
    </div>`;

    // Job count
    html += `<div class="job-count">${this._filteredJobs.length} open position${this._filteredJobs.length !== 1 ? 's' : ''}</div>`;

    // Job list (accordion)
    // Sort: beta jobs last
    const sortedJobs = [...this._filteredJobs].sort((a, b) => {
      const aBeta = a.slug === 'cloud-security-engineer' ? 1 : 0;
      const bBeta = b.slug === 'cloud-security-engineer' ? 1 : 0;
      return aBeta - bBeta;
    });

    html += `<div class="job-list">`;
    for (const j of sortedJobs) {
      const isExpanded = this._expandedSlug === j.slug;
      const isApplying = this._applyingSlug === j.slug;
      // isVerifying removed — candidates don't see verification status

      const isBeta = j.slug === 'cloud-security-engineer'; // Beta: no resume required
      html += `<div class="job-card ${isExpanded ? 'expanded' : ''} ${isBeta ? 'job-beta' : ''}" data-slug="${esc(j.slug)}">`;
      if (isBeta) html += `<div class="beta-badge">BETA</div>`;

      // Header row (always visible, clickable)
      html += `<div class="job-card-header" data-toggle="${esc(j.slug)}">
        <div class="job-card-left">
          <div class="job-title">${esc(j.title)}</div>
          <div class="job-meta">
            <span class="job-meta-item">${esc(j.location || 'Location TBD')}</span>
            ${j.workType ? `<span class="job-meta-sep">·</span><span class="job-meta-item">${esc(j.workType)}</span>` : ''}
            ${j.clearance && j.clearance !== 'None' ? `<span class="clearance-badge">${esc(j.clearance)}</span>` : ''}
            ${j.salaryMin || j.salaryMax ? `<span class="salary">${formatSalary(j.salaryMin, j.salaryMax, j.salaryType)}</span>` : ''}
          </div>
          ${j.skills.length > 0 ? `<div class="job-skills">${j.skills.slice(0, 6).map(s => `<span class="skill-tag">${esc(s)}</span>`).join('')}</div>` : ''}
        </div>
        <span class="job-chevron ${isExpanded ? 'open' : ''}">▸</span>
      </div>`;

      // Expanded content
      if (isExpanded) {
        html += `<div class="job-expanded">`;

        if (isApplying) {
          // Show JD above the form
          if (j.description) {
            html += `<div class="detail-description" style="margin-bottom:20px">${j.description}</div>`;
          }
          // Beta jobs get no-resume form, regular jobs require CV
          html += isBeta ? this._renderBetaApplyForm(j) : this._renderApplyFormInline(j);
        } else {
          // Job detail + apply button
          if (j.description) {
            html += `<div class="detail-description">${j.description}</div>`;
          }
          html += `<button class="detail-apply-btn" data-apply="${esc(j.slug)}">Apply Now</button>`;
        }

        html += `</div>`;
      }

      // Powered by Beskar on each card
      html += `<div class="job-powered">⬡ Powered by <a href="https://beskar.io" target="_blank" rel="noopener">Beskar</a></div>`;
      html += `</div>`; // close job-card
    }
    html += `</div>`; // close job-list

    if (this._filteredJobs.length === 0) {
      html += `<div class="empty"><div class="empty-icon">⬡</div>No jobs match your search.</div>`;
    }

    // Footer
    html += `<div class="footer"><span class="footer-text">Powered by <a href="https://beskar.io" target="_blank" rel="noopener">Beskar</a></span></div>`;
    html += `</div>`; // close wrapper

    root.innerHTML = html;
    this._bindEvents();
  }

  private _renderApplyFormInline(j: Job): string {
    let html = `<div class="apply-form">
      <div class="apply-subtitle">Your information</div>
      <form id="apply-form-${esc(j.slug)}" enctype="multipart/form-data">
        <div class="form-row">
          <div class="form-group"><label class="form-label">Full Name *</label><input class="form-input" name="name" required placeholder="Your full name" autocomplete="name"></div>
          <div class="form-group"><label class="form-label">Email *</label><input class="form-input" name="email" type="email" required placeholder="you@email.com" autocomplete="email"></div>
        </div>
        <div class="form-group"><label class="form-label">Resume *</label>
          <div class="resume-drop" data-resume-drop="${esc(j.slug)}" style="padding:20px;border:1.5px dashed var(--border);border-radius:6px;text-align:center;font-size:13px;color:var(--muted);cursor:pointer;transition:all 150ms">
            Drop your resume here or click to upload
            <input type="file" name="resume" accept=".pdf,.docx,.doc" style="display:none">
          </div>
        </div>
        <div class="form-group"><label class="form-label">LinkedIn (optional)</label><input class="form-input" name="linkedin" placeholder="linkedin.com/in/yourname"></div>`;

    for (const q of j.questions) {
      html += `<div class="form-group"><label class="form-label">${esc(q.label)} ${q.required ? '*' : ''}</label>`;
      if (q.type === 'textarea') html += `<textarea class="form-input" data-question-id="${esc(q.id)}" rows="3" ${q.required ? 'required' : ''} style="resize:vertical"></textarea>`;
      else if (q.type === 'select' && q.options) html += `<select class="form-select" data-question-id="${esc(q.id)}" ${q.required ? 'required' : ''}><option value="">Select...</option>${q.options.map(o => `<option value="${esc(o)}">${esc(o)}</option>`).join('')}</select>`;
      else html += `<input class="form-input" data-question-id="${esc(q.id)}" ${q.required ? 'required' : ''}>`;
      html += `</div>`;
    }

    // Honeypot
    html += `<div style="position:absolute;left:-9999px;opacity:0"><input name="website" tabindex="-1" autocomplete="off"></div>`;

    html += `<label class="form-consent"><input type="checkbox" name="consent"><span class="form-consent-text">I consent to having my professional identity verified using publicly available data sources.</span></label>
      <button type="submit" class="form-submit">Apply Now</button>
      </form></div>`;
    return html;
  }

  private _showSubmitted(job: Job, shareCode?: string): void {
    this._expandedSlug = job.slug;
    this._renderAll();
    const root = this.shadowRoot!;
    const expanded = root.querySelector(`.job-card[data-slug="${job.slug}"] .job-expanded`);
    if (!expanded) return;

    const orgName = esc(this._config?.org.name || 'The employer');
    // Always route applicants to the gap-fill / Alloy ID surface after apply,
    // regardless of how strong the verification was. Captures every applicant
    // into the Alloy ID flow so they can build out their verified profile.
    const improveUrl = shareCode
      ? `https://id.beskar.io/improve.html?code=${encodeURIComponent(shareCode)}`
      : 'https://id.beskar.io/';
    const ctaHtml = `
      <a class="complete-cta" href="${improveUrl}" target="_blank" rel="noopener">
        Build your Alloy ID →
      </a>
      <div class="complete-cta-sub">
        Add public proof points (LinkedIn, GitHub, certifications) to strengthen your verification — works for any future application.
      </div>`;

    expanded.innerHTML = `
      <div class="complete">
        <div class="complete-hex">✓</div>
        <div class="complete-title">Application submitted</div>
        <p class="complete-sub">Thank you for applying. ${orgName} will review your profile and be in touch.</p>
        ${ctaHtml}
      </div>`;
  }

  /** Beta apply form — no resume, just name + email + employer + city + clearance */
  private _renderBetaApplyForm(j: Job): string {
    let html = `<div class="apply-form">
      <div class="apply-subtitle" style="color:var(--brand)">No resume required — we verify from public sources</div>
      <form id="apply-form-${esc(j.slug)}">
        <div class="form-row">
          <div class="form-group"><label class="form-label">Full Name *</label><input class="form-input" name="name" required placeholder="Your full name" autocomplete="name"></div>
          <div class="form-group"><label class="form-label">Email *</label><input class="form-input" name="email" type="email" required placeholder="you@email.com" autocomplete="email"></div>
        </div>
        <div class="form-row">
          <div class="form-group"><label class="form-label">Current Employer *</label><input class="form-input" name="employer" required placeholder="Company name" autocomplete="organization"></div>
          <div class="form-group"><label class="form-label">City *</label><input class="form-input" name="city" required placeholder="City or metro" autocomplete="address-level2"></div>
        </div>
        <div class="form-group"><label class="form-label">LinkedIn (optional)</label><input class="form-input" name="linkedin" placeholder="linkedin.com/in/yourname"></div>`;
    html += `<div style="position:absolute;left:-9999px;opacity:0"><input name="website" tabindex="-1" autocomplete="off"></div>`;
    html += `<label class="form-consent"><input type="checkbox" name="consent"><span class="form-consent-text">I consent to having my professional identity verified using publicly available data sources.</span></label>
      <button type="submit" class="form-submit" style="background:var(--brand)">Apply Now</button>
      </form></div>`;
    return html;
  }

  // ── Event Binding ──

  private _bindEvents(): void {
    const root = this.shadowRoot!;
    const debouncedFilter = debounce(() => this._applyFilters(), 200);

    // Search
    root.getElementById('search')?.addEventListener('input', (e) => {
      this._searchQuery = (e.target as HTMLInputElement).value;
      debouncedFilter();
    });

    // Clearance filter
    root.getElementById('filter-clearance')?.addEventListener('change', (e) => {
      this._filterClearance = (e.target as HTMLSelectElement).value;
      this._applyFilters();
    });

    // Accordion toggles
    root.querySelectorAll('[data-toggle]').forEach(el => {
      el.addEventListener('click', () => {
        this._toggleJob((el as HTMLElement).dataset.toggle!);
      });
    });

    // Apply buttons
    root.querySelectorAll('[data-apply]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        this._showApplyForm((el as HTMLElement).dataset.apply!);
      });
    });

    // Resume drop zones
    root.querySelectorAll('[data-resume-drop]').forEach(dropZone => {
      const fileInput = dropZone.querySelector('input[type="file"]') as HTMLInputElement;
      dropZone.addEventListener('click', () => fileInput?.click());
      dropZone.addEventListener('dragover', (e) => { (e as DragEvent).preventDefault(); (dropZone as HTMLElement).style.borderColor = 'var(--brand)'; (dropZone as HTMLElement).style.color = 'var(--brand)'; });
      dropZone.addEventListener('dragleave', () => { (dropZone as HTMLElement).style.borderColor = ''; (dropZone as HTMLElement).style.color = ''; });
      dropZone.addEventListener('drop', (e) => {
        (e as DragEvent).preventDefault();
        (dropZone as HTMLElement).style.borderColor = '';
        const files = (e as DragEvent).dataTransfer?.files;
        if (files?.[0] && fileInput) { fileInput.files = files; fileInput.dispatchEvent(new Event('change')); }
      });
      fileInput?.addEventListener('change', () => {
        const existing = dropZone.querySelector('.resume-name');
        if (existing) existing.remove();
        if (fileInput.files?.[0]) {
          const span = document.createElement('span');
          span.className = 'resume-name';
          span.style.cssText = 'display:block;margin-top:8px;color:var(--brand);font-size:12px';
          span.textContent = '✓ ' + fileInput.files[0].name;
          dropZone.appendChild(span);
        }
      });
    });

    // Apply form submissions
    root.querySelectorAll('form[id^="apply-form-"]').forEach(form => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const slug = form.id.replace('apply-form-', '');
        const job = this._config?.jobs.find(j => j.slug === slug);
        if (job) this._handleApply(form as HTMLFormElement, job);
      });
    });
  }
}

// ── Register ──
customElements.define('beskar-jobs', BeskarJobs);

// ── Auto-init from script tag ──
(function autoInit() {
  const currentScript = document.currentScript || document.querySelector('script[data-org]');
  if (currentScript) {
    const org = currentScript.getAttribute('data-org');
    if (org && !document.querySelector('beskar-jobs')) {
      const el = document.createElement('beskar-jobs');
      el.setAttribute('org', org);
      currentScript.parentNode?.insertBefore(el, currentScript);
    }
  }
})();

export { BeskarJobs };
